import type { TFunction } from 'i18next';
import { MemberRole, type Member } from '../../types';
import { AsyncCombobox } from '@/shared/components/custom/async-combobox/AsyncCombobox';
import type { AsyncComboboxOption } from '@/shared/components/custom/async-combobox/AsyncCombobox.types';
import { useAssignMemberArea } from '../../hooks/useAssignMemberArea';
import { useMemberCatalogs } from '../../hooks/useMemberCatalogs';
import { useActiveOrganization } from '@/features/organizations/public';
import { useToast } from '@/shared/hooks/useToast';
import { getColorValue, getTextColorFromToken } from '@/core/lib/colors';

interface AreaCellProps {
  member: Member;
  t: TFunction;
}

function renderAreaBadge(option: AsyncComboboxOption) {
  const colorValue = option.color ? getColorValue(option.color) : '';
  const textColor = option.color ? getTextColorFromToken(option.color) : undefined;

  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
      style={{
        background: colorValue || 'hsl(var(--muted))',
        color: textColor ?? 'hsl(var(--muted-foreground))',
      }}
    >
      {option.label}
    </span>
  );
}

export function AreaCell({ member, t }: AreaCellProps) {
  const { mutate: assignArea, isPending } = useAssignMemberArea();
  const { toast } = useToast();
  const { areas } = useMemberCatalogs();
  const { activeOrganization } = useActiveOrganization();

  const currentUserRole = activeOrganization?.role as MemberRole | undefined;
  const isEditable = currentUserRole === MemberRole.Owner || currentUserRole === MemberRole.Admin;

  const staticOptions: AsyncComboboxOption[] = areas.map((a) => ({
    id: a.id,
    label: a.name ?? t('area.none'),
    color: a.color ?? undefined,
  }));

  const selectedOption = staticOptions.find((o) => o.id === member.areaId);

  const handleChange = (areaId: string | null) => {
    if (areaId === member.areaId) return;
    assignArea(
      { memberId: member.id, areaId },
      {
        onSuccess: () => toast.success({ title: t('area.assignSuccess') }),
      },
    );
  };

  return (
    <div data-testid={`members-area-cell-${member.id}`}>
      <AsyncCombobox
        mode="single"
        queryKey={['area-cell', member.id]}
        staticOptions={staticOptions}
        value={member.areaId}
        onChange={handleChange}
        placeholder={t('area.none')}
        searchPlaceholder={t('area.search')}
        emptyMessage={t('area.none')}
        clearable
        disabled={!isEditable || isPending}
        selectedOptions={selectedOption ? [selectedOption] : []}
        renderValue={renderAreaBadge}
        triggerClassName="w-auto border-0 shadow-none bg-transparent hover:bg-transparent p-0 h-auto ring-0 focus-visible:ring-0 focus-visible:border-0"
      />
    </div>
  );
}
